import { ButtonTypeMap } from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import { SxProps } from '@mui/joy/styles/types';
import { SystemStyleObject } from '@mui/system';

import { ReactNode, useState } from 'react';

import { cn } from '@/utils/tailwind';

export function ButtonSwitch({
  children,
  sx,
  withSpacing,
}: {
  children: ReactNode;
  sx?: SystemStyleObject;
  withSpacing?: boolean;
}) {
  const defaultSx: SxProps = (theme) => ({
    outline: `1px solid ${theme.vars.palette.divider}`,
    outlineOffset: '-1px',
  });
  return (
    <ButtonGroup
      size="sm"
      sx={sx ? [defaultSx, sx] : defaultSx}
      spacing={withSpacing ? '0.1rem' : 0}
    >
      {children}
    </ButtonGroup>
  );
}

export function ButtonSwitchSimple<V = string>({
  value,
  onChange,
  options,
  sx,
  className,
}: {
  value: V | V[];
  onChange: (value: V) => void | Promise<void>;
  options: {
    value: V;
    label: string;
    props?: ButtonTypeMap['props'];
  }[];
  sx?: SystemStyleObject;
  className?: string;
}) {
  const [pending, setPending] = useState(false);
  return (
    <ButtonSwitch sx={{ ...sx, padding: '0.2rem' }} withSpacing>
      {options.map((o, i) => {
        const isSelected = Array.isArray(value)
          ? value.includes(o.value)
          : value === o.value;
        return (
          <button
            key={typeof o.value === 'string' ? o.value : i}
            variant={isSelected ? 'solid' : 'plain'}
            onClick={() => {
              const result = onChange(o.value);
              if (
                typeof result === 'object' &&
                !!result &&
                'then' in result &&
                typeof result.then === 'function'
              ) {
                setPending(true);
                result.then(
                  () => {
                    setPending(false);
                  },
                  () => {
                    setPending(false);
                  }
                );
              }
            }}
            {...o.props}
            disabled={pending}
            className={cn(
              'rounded-md px-3 py-1 text-xs font-medium text-primary hover:bg-secondary-hover',
              isSelected ? 'bg-tertiary' : 'bg-transparent',
              className
            )}
            type="button"
          >
            {o.label}
          </button>
        );
      })}
    </ButtonSwitch>
  );
}
